/**
 * Storefront PREVIEW requests -- every API call a preview page makes says so.
 *
 * The store editor previews a store at `/store/<slug>?preview=1` (the drawer
 * iframe, and the "open in a new tab" pop-out for a store that is not live).
 * For a store that does NOT admit shoppers (a draft, not launched), the API
 * only answers an OWNER preview: the request must carry the `?preview` flag
 * AND a bearer of the store's own tenant staff or a Super Admin
 * (`storefront_access.caller_may_owner_preview` on the backend). The bearer
 * already rides every request; this module adds the flag.
 *
 * Why here and not per call: the storefront page makes many independent reads
 * (the session gate's entry lookup, the chrome, nav, category, product,
 * search, quote), each resolving the store on its own. Before this, none of
 * them carried the flag, so the preview of a draft store showed "Store not
 * found" (Jake, 2026-10-22). One interceptor step keeps every current and
 * future storefront read in step with the page.
 *
 * Scope: only while the ADDRESS BAR is a storefront page (`/shop/<slug>...` or
 * the legacy `/store/<slug>...`) whose own query says preview (same rule as
 * `isStorefrontPreview`, so `?preview=0` opts out). The page keeps that query
 * across in-preview navigation (`installPreviewQueryPersistence`). A request
 * that states its own `preview` value is left alone. The flag grants nothing
 * by itself: an anonymous visitor or another tenant gets the not-found answer
 * either way.
 *
 * THIS MODULE MUST STAY A LEAF apart from storefrontSlug.js and
 * storefrontPreview.js (both leaves): `@/axios` imports it while still
 * evaluating (see __tests__/axios-import-cycle.spec.js).
 */
import { currentStorefrontSlug } from "./storefrontSlug";
import { STOREFRONT_PREVIEW_PARAM, isStorefrontPreview } from "./storefrontPreview";

/** True when the current page is a storefront page in preview mode. */
export function currentPageIsStorefrontPreview() {
    if (typeof window === "undefined" || !window.location) return false;
    if (currentStorefrontSlug() === null) return false;
    const value = new URLSearchParams(window.location.search).get(
        STOREFRONT_PREVIEW_PARAM,
    );
    return isStorefrontPreview({
        query: { [STOREFRONT_PREVIEW_PARAM]: value === null ? undefined : value },
    });
}

function statesItsOwnPreview(config) {
    if (config.params && config.params[STOREFRONT_PREVIEW_PARAM] !== undefined) {
        return true;
    }
    const url = typeof config.url === "string" ? config.url : "";
    return new RegExp(`[?&]${STOREFRONT_PREVIEW_PARAM}=`).test(url);
}

/** Request-interceptor step: add `preview=1` on a storefront preview page. */
export function applyStorefrontPreviewParam(config) {
    if (!config || statesItsOwnPreview(config)) return config;
    if (!currentPageIsStorefrontPreview()) return config;
    config.params = { ...(config.params || {}), [STOREFRONT_PREVIEW_PARAM]: "1" };
    return config;
}
